'use client';

import MinusCircleSvg from '@/assets/svgs/jobs/minus-circle.svg';
import PlusCircleSvg from '@/assets/svgs/plus-circle.svg';
import { inter500 } from '@/config/fonts';
import {
  addLinkToInitialMessage,
  removeTrackedLink,
} from '@/containers/Jobs/redux/jobEditors.slice';
import { JobTrackedLink } from '@/containers/Jobs/types';
import { Modals } from '@/containers/Modals/all-modals';
import { useAppDispatch } from '@/lib/redux/store';
import IconButton from '@/shared/components/Buttons/IconButton/IconButton';
import classNames from 'classnames';
import { addOpenModal } from '../redux/modals.slice';

interface TrackedLinksListProps {
  className?: string;
  jobId: string;
  links: JobTrackedLink[];
  showEmptyContent?: boolean;
  allowInsertion?: boolean;
  hideAddButton?: boolean;
  size?: 'sm' | 'md';
  onInsert?: () => void;
}

const TrackedLinksList = ({
  className,
  jobId,
  links,
  showEmptyContent,
  allowInsertion,
  hideAddButton,
  onInsert,
  size = 'md',
}: TrackedLinksListProps) => {
  const dispatch = useAppDispatch();
  if (!links.length && showEmptyContent) {
    return (
      <div className={classNames('text-gray-default text-center text-sm', className)}>
        Start by adding a few links
      </div>
    );
  }
  const fontClassName = size === 'md' ? 'text-[14px]' : 'text-[12px]';
  return (
    <div className={classNames('', className)}>
      <table className="w-full table-auto">
        <thead>
          <tr>
            <th
              className={classNames(
                'text-gray-default h-12 text-left font-normal',
                fontClassName
              )}>
              <div className="flex items-center">Domain Name</div>
            </th>
            <th
              className={classNames(
                'text-gray-default h-12 text-left font-normal',
                fontClassName
              )}>
              <div className="flex items-center">Slash Tag</div>
            </th>
            <th
              className={classNames(
                'text-gray-default h-12 text-left font-normal',
                fontClassName
              )}>
              <div className="flex items-center">Link</div>
            </th>
            <th
              className={classNames(
                'text-gray-default h-12 w-[120px] text-left font-normal',
                fontClassName
              )}>
              {allowInsertion && !hideAddButton ? (
                <button
                  onClick={() => {
                    dispatch(
                      addOpenModal({
                        modalName: Modals.AddTrackedLinkModal,
                        modalVariant: 'narrow',
                        modalParams: JSON.stringify({
                          jobId,
                          allowInsertion: allowInsertion,
                        }),
                      })
                    );
                    onInsert?.();
                  }}
                  title="Add a link"
                  className="btn btn-primary btn-sm flex gap-1">
                  <PlusCircleSvg className="h-5 w-5" /> Add a link
                </button>
              ) : (
                <div className="flex items-center">Actions</div>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {links.map((link, idx) => {
            return (
              <tr key={idx}>
                <td className="border-t-[1px] border-t-gray-200 dark:border-t-gray-800">
                  <div
                    className={classNames(
                      'text-base-content flex h-14 items-center',
                      fontClassName,
                      inter500.className
                    )}>
                    {link.domain_name}
                  </div>
                </td>
                <td className="border-t-[1px] border-t-gray-200 dark:border-t-gray-800">
                  <div
                    className={classNames(
                      'text-base-content flex h-14 items-center',
                      fontClassName,
                      inter500.className
                    )}>
                    {link.slash_tag ? `/${link.slash_tag}` : 'N/A'}
                  </div>
                </td>
                <td className="border-t-[1px] border-t-gray-200 dark:border-t-gray-800">
                  <div
                    className={classNames(
                      'text-base-content flex h-14 items-center',
                      fontClassName,
                      inter500.className
                    )}>
                    https://{link.domain_name}/{link.slash_tag || '{dynamic}'}
                  </div>
                </td>
                <td className="border-t-[1px] border-t-gray-200 dark:border-t-gray-800">
                  <div
                    className={classNames(
                      'flex h-14 items-center gap-2',
                      inter500.className
                    )}>
                    {allowInsertion && (
                      <IconButton
                        className="btn-outline border-blue-200 shadow-sm"
                        title="Insert"
                        tooltip="Insert tracked link into message"
                        icon={<PlusCircleSvg className="h-6 w-6" />}
                        onClick={() => {
                          dispatch(addLinkToInitialMessage({ jobId, link }));
                          onInsert?.();
                        }}
                      />
                    )}
                    <IconButton
                      className="btn-outline border-blue-200 shadow-sm"
                      title="Remove"
                      tooltip="Remove tracked link"
                      icon={<MinusCircleSvg />}
                      onClick={() => {
                        dispatch(removeTrackedLink({ jobId, linkId: link.id }));
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TrackedLinksList;
